import { environment, formatDate, getDeviceId, getOnlineServices, getPlatformFromUserAgent, skipCheckChromeExtension, sleep } from "../util/index";
import { indexDBStorage } from "../storage/indexDBStorage";
import { useDispatch, useSelector } from "react-redux";
import { createFailureAction, createSetReleaseVersionAction, createSetViewAction, createUpdatePreChacheValidationStatusAction, createUpdateProgressAction, createUpdateStatusAction } from "../state/reducer";
import { IADPState,UpdateRequest, SoftwareUpdateStatusRequest, Manifest, FailureType, pingResults } from "../typings";
import { useFetchService } from "./useFetchService";
import { HEADER_ENAEP, NOT_AVAILABLE } from "../util/constants";
import { fetchResources } from "../services/fetchResources";
import { useIPCBridge } from "./useIPCBridge";

const logPrefix = "[ADP Student App][UpdatesServiceHook]"

export function useUpdatesService() {
  const dispatch = useDispatch();
  const {fetch} = useFetchService();
  const {sendPing,sendGreeting,getSoftwareStatusValues} = useIPCBridge();
  const state = useSelector((state:{rootState:IADPState}) => state.rootState)
  async function resetSW() {
    let promises = [];
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const reg of regs) {
      promises.push(reg.unregister());
    }
    await Promise.all(promises).finally(() => {
      dispatch(createUpdatePreChacheValidationStatusAction({validationStatus:"Pending",
      validationDetails:`Reseting service workers and reloading window`})); 
      setTimeout((()=>{
        window.location.reload();
      }),2000);
    });
  }
    async function FetchReleaseManifest(onSoftwareUpdateResponse:(response:any)=>void){
      const onlineServiceConfig = getOnlineServices(state.appState.configuration);
      const versionPayload:UpdateRequest = {
        "environment": state.appState.environment || "dev",
        "product": "enaep",
        "requestType": "ManifestOnly",
        "appCode": "ng-enaep-secure-browser",
        "targetedOS": "chromeOS",
        "locale": "en-US",
        "installedVersion": state.appState.releaseVersion || "",
        "targetedVersion": "",
        "targetedBuildNumber": "",
        "buildNumber": state.appState?.releaseBuildNumber?.toString(),
        "clientDetails": {
          "ip": state.sessionState.networkDetails?.address || "127.0.0.1",
          "macAddress": state.sessionState.networkDetails?.address || "127.0.0.1",
          "deviceDetails": {
              "deviceType": state.deviceState.deviceType || "SBE",
              "deviceName":state.deviceState.deviceId  || "Chromebook"
          }
      }
      };
      const url = `${environment.resolveApiDomain(state.appState.configuration)}/${onlineServiceConfig?.softwareUpdate}`;
      //RequestService.postSoftwareUpdateInquiry(versionPayload,url, onSoftwareUpdateResponse);
      const response = await fetch(url,{headers:HEADER_ENAEP,method: "POST",body:JSON.stringify(versionPayload)});
      if(response){
        onSoftwareUpdateResponse(response);
        return true;
      }
      return false;
    }
    const updateStatusPayload =(taskStatus:"SUCCESS" | "FAILURE")=> {
      const payload: SoftwareUpdateStatusRequest = {
       context:{
         path: `diagnostic/ng-enaep-student-app/${state.deviceState?.deviceType?.toLocaleLowerCase() || "sbe"}/p1/software-update-status/${state.deviceState?.deviceId || getDeviceId()}` ,  
         fileName:`ADP_PWA_UPDATE_${taskStatus}_${formatDate(new Date(),true)}.json`,  
       },
        data:{
         schemaVersion: '3.0.3',
         releaseVersion: state.appState?.releaseVersion || "",
         buildNumber: state.appState?.releaseBuildNumber.toString() || "0",
         appVersion: state.appState?.appVersion,
         appType: 'ng-enaep-student-app',
         appName: 'ng-enaep-adp-pwa',
         deviceDetails: {
           deviceType:state.deviceState?.deviceType || "SBE",
           deviceIdType: state.deviceState?.deviceIdType || "NotAvailable",
           deviceName: state.deviceState?.deviceId  || "Chromebook",
           deviceModel: state.deviceState?.deviceModel  || "Chromebook",
           operatingSystem: getPlatformFromUserAgent(),
           hostingPlatform: state.appState.runtimePlatform
         },
         sessionDetails: {
           clientLaunchId: state.appState?.appLaunchId as string,
           adpLaunchDateTime:state.appState.appLaunchTime || "NotAvailable",
           setupCode:'NotAvailable',
           verificationCode: "NotAvailable",
           appAccessedFor: "NotSelected",
           participantId: 'NotAvailable',
           assessmentDeliveryMode: state.sessionState?.selectedAssessmentMode,
           connectionType:state.deviceState.deviceType === "SBE" ? "Internet": "NPN",
           serverDetails: environment.resolveApiDomain(state.appState.configuration),
         },
         updateDetails: {
           updateType: "ADP-PWA-Update",
           updateStartDateTime: new Date().toISOString(),
           updateEndDateTime:new Date().toISOString(),
           updateExecutionTime: 0,
           updateStatus: taskStatus,
           appInstallationMethod:"NotAvailable",
           newReleaseVersion: "",
           appComponents:[]
        } 
       }
      }
       
      return payload;
    
   }
    async function OnUpdateError(reason: string, manifest?: Manifest){
      const updateRecord = await (await indexDBStorage.getInstance()).transactions.get("adp-session-references","updateRecord");
      console.error(logPrefix, `update failed after ${updateRecord?.updateAttempts} attempts`);
      const onlineServiceConfig = getOnlineServices(state.appState.configuration);
      let payload = updateStatusPayload("FAILURE");
      payload.data.releaseVersion = updateRecord?.localVersion;
      payload.data.updateDetails.newReleaseVersion = 'FailedToUpdate';
      payload.data.updateDetails.updateEndDateTime =new Date().toISOString();
      payload.data.updateDetails.updateStartDateTime =updateRecord?.updateStartTime;
      payload.data.updateDetails.updateExecutionTime = updateRecord ? Date.now()-updateRecord?.timestamp : 0;
      payload.data.updateDetails.appComponents = state.updateState?.appComponentVersions;
      payload.data.updateDetails.updateFailure ={
        currentReleaseVersion:  payload.data.releaseVersion,
        currentBuildVersion:  payload.data.buildNumber?.toString(),
        targetedReleaseVersion: manifest?.manifest?.product?.version || "unknown",
        targetedBuildVersion: manifest?.manifest?.product?.buildNumber || "unknown",
        reasons: [{
          componentName: "adp-pwa",
          componentVersion: state.appState?.appVersion,
          error: reason
        }]
    };
    if(state.deviceState?.deviceId && state.appState?.extensionVersion !== NOT_AVAILABLE){
      console.log("[Ping] we have the device id and chrome extension version.",state.deviceState?.deviceId,state.appState?.extensionVersion);
      const url = `${environment.resolveApiDomain(state.appState.configuration)}/${onlineServiceConfig?.softwareStatus}`;
      fetch(url,{headers:HEADER_ENAEP,method: "POST",body:JSON.stringify(payload)},"UpdateError").then(async (response)=>{
          if(response){
            console.info(logPrefix,"software status update posted");
          }
          else {
            console.error(logPrefix,"failed to post software status");
            (await indexDBStorage.getInstance()).transactions.put("adp-session-references",({payload:payload, error:"failed to post software status",attempts:updateRecord?.updateAttempts}),`${state.appState.appLaunchId}-updateStatusFailure`); 
            (await indexDBStorage.getInstance()).transactions.put("adp-session-references",({payload:payload, error:reason,attempts:updateRecord?.updateAttempts}),`${state.appState.appLaunchId}-updateFailure`); 
            (await indexDBStorage.getInstance()).transactions.put("adp-session-references",{localVersion:state.appState?.releaseVersion,updateAttempts:0},"updateRecord");
          }
        });
        dispatch(createFailureAction("UpdateError")); 
        (await indexDBStorage.getInstance()).transactions.delete("adp-session-references","updateRecord")
    }
    else{
      console.log("[Ping] we dont have the device id or the chrome extension version.",state.deviceState?.deviceId,state.appState?.extensionVersion);
      const index = "status"
      if(skipCheckChromeExtension()){
        console.log("[Ping] skipping the check for chrome extension.");
      }
      else{
        sendPing(index).then(async (result:pingResults)=>{
          if(result === "success"){
            sendGreeting()
            const updatedValues = await getSoftwareStatusValues()
            payload.data.deviceDetails.deviceName = updatedValues.deviceId;
            payload.data.deviceDetails.deviceType = updatedValues.deviceType;
            payload.data.deviceDetails.deviceModel = updatedValues.deviceModel;
            payload.data.deviceDetails.hostingPlatform = updatedValues.runtimePlatform;
            const url = `${environment.resolveApiDomain(state.appState.configuration)}/${onlineServiceConfig?.softwareStatus}`;
            fetch(url,{headers:HEADER_ENAEP,method: "POST",body:JSON.stringify(payload)},"UpdateError").then(async (response)=>{
              if(response){
                console.info(logPrefix,"software status update posted");
              }
              else {
                console.error(logPrefix,"failed to post software status");
                (await indexDBStorage.getInstance()).transactions.put("adp-session-references",({payload:payload, error:"failed to post software status",attempts:updateRecord?.updateAttempts}),`${state.appState.appLaunchId}-updateStatusFailure`); 
                (await indexDBStorage.getInstance()).transactions.put("adp-session-references",({payload:payload, error:reason,attempts:updateRecord?.updateAttempts}),`${state.appState.appLaunchId}-updateFailure`); 
                (await indexDBStorage.getInstance()).transactions.put("adp-session-references",{localVersion:state.appState?.releaseVersion,updateAttempts:0},"updateRecord");
              }
            });
            dispatch(createFailureAction("UpdateError")); 
            (await indexDBStorage.getInstance()).transactions.delete("adp-session-references","updateRecord")
          }
          else{
            console.error(logPrefix,"failed to post software status due to missing chrome extesion.");
            (await indexDBStorage.getInstance()).transactions.put("adp-session-references",({payload:payload, error:"failed to post software status",attempts:updateRecord?.updateAttempts}),`${state.appState.appLaunchId}-updateStatusFailure`); 
            (await indexDBStorage.getInstance()).transactions.put("adp-session-references",({payload:payload, error:reason,attempts:updateRecord?.updateAttempts}),`${state.appState.appLaunchId}-updateFailure`); 
            (await indexDBStorage.getInstance()).transactions.put("adp-session-references",{localVersion:state.appState?.releaseVersion,updateAttempts:0},"updateRecord");
            if(state.appState.configuration?.ping.failurePoints.includes(index)){
              dispatch(state.appState.configuration?.ping.errorScreen.includes("WarningView") ? createSetViewAction("WarningView") : createFailureAction(state.appState.configuration?.ping.errorScreen ? state.appState.configuration?.ping.errorScreen as FailureType : "ApplicationError"));
            }
            else{
              dispatch(createFailureAction("UpdateError"));
            }
          }
        })
      }
    }
    }
    async function ReleaseStatus(){
      return new Promise(async ():Promise<void> => {
      try {
        const onSoftwareUpdateResponse = async (response:any)=>{
         
          const manifest: Manifest = response;
          const onlineServiceConfig = getOnlineServices(state.appState.configuration);
          console.log(logPrefix, "release status", manifest.infoCode);
          const updateRecord = await (await indexDBStorage.getInstance()).transactions.get("adp-session-references","updateRecord");
         if(response.error){
          if(updateRecord?.updateAttempts > 3){
              await OnUpdateError(`Error: ${response?.error?.message}, update failed after ${updateRecord?.updateAttempts} attempts`);
              return;
           }
           else {
            //store update attempts
            (await indexDBStorage.getInstance()).transactions.put("adp-session-references",
          {localVersion:state.appState?.releaseVersion,newVersion:manifest.manifest?.product?.version,timestamp:Date.now(),
            updateStartTime:updateRecord?.updateStartTime ? updateRecord?.updateStartTime : new Date().toISOString(),
            updateAttempts:updateRecord?.updateAttempts ? updateRecord?.updateAttempts+1 : 1, error:response?.error?.message},"updateRecord");
            //retry software update
           await sleep(500);
           await FetchReleaseManifest(onSoftwareUpdateResponse);
           }
          return;
        }
       if(manifest.infoCode=== "INFO-6001"){
          if(state.appState.releaseVersion === manifest.manifest?.product?.version){
            console.log(logPrefix, "no new release available", {localVersion:state.appState.releaseVersion, releaseVersion:manifest.manifest?.product?.version});
            const updateRecord = await (await indexDBStorage.getInstance()).transactions.get("adp-session-references","updateRecord");
            //post success after at least one attempt
            if(!updateRecord || updateRecord?.updateAttempts > 0){
              if(!updateRecord){
                console.info(logPrefix, "No update records in the IndexDB, considered as new installation, post update status");
               }
              else {
                console.log(logPrefix, "posting update status after success update", {localVersion:state.appState.releaseVersion, releaseVersion:manifest.manifest?.product?.version});
                dispatch(createUpdateProgressAction({type: "update-not-available"})); 
              } 
              const payload = updateStatusPayload("SUCCESS");
              payload.data.releaseVersion = updateRecord?.localVersion;
              payload.data.updateDetails.newReleaseVersion = manifest.manifest?.product?.version;
              payload.data.updateDetails.updateEndDateTime = new Date().toISOString();
              payload.data.updateDetails.updateStartDateTime =updateRecord?.updateStartTime;
              payload.data.updateDetails.updateExecutionTime = updateRecord ? Date.now()-updateRecord?.timestamp : 0;
              payload.data.updateDetails.appComponents = {...state.serviceWorkers,...state.cacheValidation}
              if(state.deviceState?.deviceId && state.appState?.extensionVersion !== NOT_AVAILABLE){
               const url = `${environment.resolveApiDomain(state.appState.configuration)}/${onlineServiceConfig?.softwareStatus}`;
               fetch(url,{headers:HEADER_ENAEP,method: "POST",body:JSON.stringify(payload)},"UpdateError").
               then(async (response)=>{
                if(response){
                  console.info(logPrefix," Software status posted");
                  (await indexDBStorage.getInstance()).transactions.put("adp-session-references",{localVersion:state.appState.releaseVersion,updateAttempts:0},"updateRecord"); 
                  await sleep(500);
                  dispatch(createSetViewAction("ModeSelectionsView"));
                  dispatch(createUpdateStatusAction("Completed"));
                }
                else {
                  console.error(logPrefix,"failed to post software status");
                  (await indexDBStorage.getInstance()).transactions.put("adp-session-references",JSON.stringify({payload:payload, error:"failed to post software status" }),"updateStatusFailure");
                }
              })
            }
            else{
              if(skipCheckChromeExtension()){
                console.log("[Ping] skipping the check for chrome extension.");
                (await indexDBStorage.getInstance()).transactions.put("adp-session-references",{localVersion:state.appState.releaseVersion,updateAttempts:0},"updateRecord"); 
                  await sleep(500);
                  dispatch(createSetViewAction("ModeSelectionsView"));
                  dispatch(createUpdateStatusAction("Completed"));
              }
              else{
              const index = "status"
              sendPing(index).then(async (result:pingResults)=>{
                if(result === "success"){
                sendGreeting()
                const updatedValues = await getSoftwareStatusValues()
                payload.data.deviceDetails.deviceName = updatedValues.deviceId;
                payload.data.deviceDetails.deviceType = updatedValues.deviceType;
                payload.data.deviceDetails.deviceModel = updatedValues.deviceModel;
                payload.data.deviceDetails.hostingPlatform = updatedValues.runtimePlatform;
                const url = `${environment.resolveApiDomain(state.appState.configuration)}/${onlineServiceConfig?.softwareStatus}`;
               fetch(url,{headers:HEADER_ENAEP,method: "POST",body:JSON.stringify(payload)},"UpdateError").
               then(async (response)=>{
                if(response){
                  console.info(logPrefix," Software status posted");
                  (await indexDBStorage.getInstance()).transactions.put("adp-session-references",{localVersion:state.appState.releaseVersion,updateAttempts:0},"updateRecord"); 
                  await sleep(500);
                  dispatch(createSetViewAction("ModeSelectionsView"));
                  dispatch(createUpdateStatusAction("Completed"));
                }
                else {
                  console.error(logPrefix,"failed to post software status");
                  (await indexDBStorage.getInstance()).transactions.put("adp-session-references",JSON.stringify({payload:payload, error:"failed to post software status" }),"updateStatusFailure");
                }
                })
              }
              else{
                console.error(logPrefix,"failed to post software status due to missing chrome extesion.");
                  (await indexDBStorage.getInstance()).transactions.put("adp-session-references",JSON.stringify({payload:payload, error:"failed to post software status" }),"updateStatusFailure");
                  if(state.appState.configuration?.ping.failurePoints.includes(index)){
                    dispatch(state.appState.configuration?.ping.errorScreen.includes("WarningView") ? createSetViewAction("WarningView") : createFailureAction(state.appState.configuration?.ping.errorScreen ? state.appState.configuration?.ping.errorScreen as FailureType : "ApplicationError"));
                  }
                  else{
                    console.info(logPrefix," Software status skipped as the extension is not available and it is configured to not fail.");
                    (await indexDBStorage.getInstance()).transactions.put("adp-session-references",{localVersion:state.appState.releaseVersion,updateAttempts:0},"updateRecord"); 
                    await sleep(500);
                    dispatch(createSetViewAction("ModeSelectionsView"));
                    dispatch(createUpdateStatusAction("Completed"));
                  }
                }
              })
            }
            }
            }
            else {
              dispatch(createSetViewAction("ModeSelectionsView"));
              dispatch(createUpdateStatusAction("Completed"));
            }
           }
           else {
            //dispatch(createSetReleaseVersionAction(manifest))
            console.log(logPrefix, "update available", {localVersion:state.appState?.releaseVersion, updateStartTime: new Date().toISOString(),releaseVersion:manifest.manifest?.product?.version});
            //store update progress in DB
            const updateRecord = await (await indexDBStorage.getInstance()).transactions.get("adp-session-references","updateRecord");
            if(!updateRecord?.updateAttempts || updateRecord?.updateAttempts === 0){
              dispatch(createUpdateProgressAction({type: "update-available"}));
              const nocache = Date.now()
              const remoteManifestData = await fetchResources(`${environment.resovleAppDomain()}manifest/naep.manifest.json?${nocache}`);
              if(!remoteManifestData.error){
                await (await indexDBStorage.getInstance()).transactions.put("adp-session-references", remoteManifestData, "manifestData")
                dispatch(createUpdateProgressAction({type: "update-ready"}));
              } 
            }
            else {
              dispatch(createUpdateProgressAction({type: "update-ready"}));
            };
            if(updateRecord?.updateAttempts === -1){
            console.log("attempt to reset cache");
                await resetSW();
           }
           else if(updateRecord?.updateAttempts > 2){
               OnUpdateError(`release version mismatch, update failed after ${updateRecord?.updateAttempts} attempts`,manifest);
             return;
              }
            (await indexDBStorage.getInstance()).transactions.put("adp-session-references",
            {localVersion:state.appState?.releaseVersion,newVersion:manifest.manifest?.product?.version,timestamp:Date.now(),
              updateStartTime:updateRecord?.updateStartTime ? updateRecord?.updateStartTime : new Date().toISOString(),
              updateAttempts:updateRecord?.updateAttempts ? updateRecord?.updateAttempts+1 : 1,error: `release version mismatch, update failed after ${updateRecord?.updateAttempts} attempts`},"updateRecord");
             //retry software update
             await (await indexDBStorage.getInstance()).transactions.delete("adp-session-references","localReleaseVersion");
             await sleep(500);
             window.location.reload();
           // OnUpdateError(`release version mismatch, update failed after ${updateRecord?.updateAttempts} attempts`,manifest);
           }
            return;
          }
          else {
            (await indexDBStorage.getInstance()).transactions.put("adp-session-references",{localVersion:state.appState.releaseVersion,updateAttempts:0},"updateRecord");
            await sleep(500);
            dispatch(createUpdateProgressAction({type: "update-not-available"}));
            await sleep(500);
            dispatch(createSetViewAction("ModeSelectionsView"));
            dispatch(createUpdateStatusAction("Completed"));
          return;
          }
        }
        FetchReleaseManifest(onSoftwareUpdateResponse)
      }
      catch (e){
        dispatch(createFailureAction("UpdateError")); 
       return;
      }
    })
    }
    return {ReleaseStatus,FetchReleaseManifest,OnUpdateError}

}


